import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../../services/api';

// --- Types ---

interface CompanySettings {
  id: number;
  name: string;
  description: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  website: string | null;
  social_links: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
  } | null;
}

interface CompanySettingsResponse {
  data: CompanySettings;
}

// --- Schema ---

const settingsSchema = z.object({
  description: z.string().max(500).optional().or(z.literal('')),
  phone: z.string().max(50).optional().or(z.literal('')),
  address: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  province: z.string().max(100).optional().or(z.literal('')),
  website: z.string().max(200).optional().or(z.literal('')),
  instagram: z.string().max(200).optional().or(z.literal('')),
  facebook: z.string().max(200).optional().or(z.literal('')),
  twitter: z.string().max(200).optional().or(z.literal('')),
});

type SettingsForm = z.infer<typeof settingsSchema>;

// --- Component ---

export default function CompanySettingsPage(): React.ReactElement {
  const { t } = useTranslation('companies');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // --- Auto-dismiss snackbar ---
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => api.get<CompanySettingsResponse>('/v1/companies/me'),
  });

  const company = settingsData?.data;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
  });

  useEffect(() => {
    if (company) {
      reset({
        description: company.description || '',
        phone: company.phone || '',
        address: company.address || '',
        city: company.city || '',
        province: company.province || '',
        website: company.website || '',
        instagram: company.social_links?.instagram || '',
        facebook: company.social_links?.facebook || '',
        twitter: company.social_links?.twitter || '',
      });
    }
  }, [company, reset]);

  const mutation = useMutation({
    mutationFn: (data: SettingsForm) => {
      const { instagram, facebook, twitter, ...rest } = data;
      const payload = {
        ...rest,
        social_links: {
          instagram: instagram || undefined,
          facebook: facebook || undefined,
          twitter: twitter || undefined,
        },
      };
      return api.patch<CompanySettingsResponse>('/v1/companies/me', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      setSnackbar({ open: true, message: t('settings.saveSuccess'), severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('settings.saveError'), severity: 'error' });
    },
  });

  const onSubmit = (data: SettingsForm): void => {
    mutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-1">{t('settings.title')}</h1>
      <p className="text-sm text-base-content/60 mb-6">{t('settings.subtitle')}</p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* General Info */}
        <div className="card bg-base-100 shadow-sm max-w-2xl mb-6">
          <div className="card-body">
            <h2 className="card-title text-lg">{t('settings.companyInfo')}</h2>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.name')}</span></label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={company?.name || ''}
                disabled
                readOnly
              />
            </div>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.description')}</span></label>
              <textarea
                className={`textarea textarea-bordered w-full ${errors.description ? 'textarea-error' : ''}`}
                rows={3}
                {...register('description')}
              />
              {errors.description && (
                <label className="label"><span className="label-text-alt text-error">{errors.description.message}</span></label>
              )}
            </div>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.phone')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.phone ? 'input-error' : ''}`}
                {...register('phone')}
              />
              {errors.phone && (
                <label className="label"><span className="label-text-alt text-error">{errors.phone.message}</span></label>
              )}
            </div>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.address')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.address ? 'input-error' : ''}`}
                {...register('address')}
              />
              {errors.address && (
                <label className="label"><span className="label-text-alt text-error">{errors.address.message}</span></label>
              )}
            </div>

            <div className="flex gap-4">
              <div className="form-control w-full mb-2">
                <label className="label"><span className="label-text">{t('settings.city')}</span></label>
                <input
                  type="text"
                  className={`input input-bordered w-full ${errors.city ? 'input-error' : ''}`}
                  {...register('city')}
                />
                {errors.city && (
                  <label className="label"><span className="label-text-alt text-error">{errors.city.message}</span></label>
                )}
              </div>
              <div className="form-control w-full mb-2">
                <label className="label"><span className="label-text">{t('settings.province')}</span></label>
                <input
                  type="text"
                  className={`input input-bordered w-full ${errors.province ? 'input-error' : ''}`}
                  {...register('province')}
                />
                {errors.province && (
                  <label className="label"><span className="label-text-alt text-error">{errors.province.message}</span></label>
                )}
              </div>
            </div>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.website')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.website ? 'input-error' : ''}`}
                {...register('website')}
              />
              {errors.website && (
                <label className="label"><span className="label-text-alt text-error">{errors.website.message}</span></label>
              )}
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="card bg-base-100 shadow-sm max-w-2xl mb-6">
          <div className="card-body">
            <h2 className="card-title text-lg">{t('settings.socialLinks')}</h2>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.instagram')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.instagram ? 'input-error' : ''}`}
                placeholder="https://instagram.com/..."
                {...register('instagram')}
              />
              {errors.instagram && (
                <label className="label"><span className="label-text-alt text-error">{errors.instagram.message}</span></label>
              )}
            </div>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.facebook')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.facebook ? 'input-error' : ''}`}
                placeholder="https://facebook.com/..."
                {...register('facebook')}
              />
              {errors.facebook && (
                <label className="label"><span className="label-text-alt text-error">{errors.facebook.message}</span></label>
              )}
            </div>

            <div className="form-control w-full mb-2">
              <label className="label"><span className="label-text">{t('settings.twitter')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.twitter ? 'input-error' : ''}`}
                placeholder="https://x.com/..."
                {...register('twitter')}
              />
              {errors.twitter && (
                <label className="label"><span className="label-text-alt text-error">{errors.twitter.message}</span></label>
              )}
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="card bg-base-100 shadow-sm max-w-2xl mb-6">
          <div className="card-body">
            <h2 className="card-title text-lg">{t('settings.branding')}</h2>
            <div className="divider mt-0 mb-2" />
            <div className="flex gap-3">
              <div className="tooltip" data-tip={t('settings.comingSoon')}>
                <button type="button" className="btn btn-outline btn-disabled" disabled>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('settings.uploadLogo')}
                </button>
              </div>
              <div className="tooltip" data-tip={t('settings.comingSoon')}>
                <button type="button" className="btn btn-outline btn-disabled" disabled>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('settings.uploadBanner')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={!isDirty || mutation.isPending}
        >
          {mutation.isPending ? <span className="loading loading-spinner loading-sm" /> : tCommon('buttons.save')}
        </button>
      </form>

      {/* Toast / Snackbar */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
